import type { ReactNode } from 'react'
import { useState } from 'react'
import { SectionCard } from '../../shared/section-card'
import { useCoachingStore } from '../../store'
import type { ReviewComment } from '../contracts'
import { CritiqueFlag } from '../critique-flag'

/**
 * High-level design section (spec §8.5, URL Shortener seed) — a
 * system-design-specific UI payload, not modeled on `ReviewSubmission` (see
 * A1's note on code-section.tsx). Pill tabs switch between the client's flow
 * and two AI-drafted alternatives; alt views tint the flow's border purple,
 * show a suggestion banner with an independent per-alt "Attach to review"
 * toggle (`hld-alt:{submissionId}:{altId}` key in the slice's `attachments`
 * — the mechanism `alternative-approaches.tsx` already established), and
 * relabel every component box's comment target from `High-level · {name}`
 * to `Alt design · {name}`. Every box (client or alt) is a comment target.
 */

interface HldBox {
  name: string
  sub: string
}

interface HldAlt {
  id: string
  label: string
  note: string
  flow: HldBox[][]
}

const CLIENT_FLOW: HldBox[][] = [
  [{ name: 'Client', sub: 'web / mobile' }],
  [{ name: 'Load balancer', sub: 'L7' }],
  [
    { name: 'Read service', sub: 'redirects' },
    { name: 'Write service', sub: 'shorten' },
  ],
  [
    { name: 'Redis cache', sub: 'cache-aside' },
    { name: 'Key-gen service', sub: 'base62 batches' },
  ],
  [{ name: 'Cassandra', sub: 'URL table' }],
]

const ALTS: HldAlt[] = [
  {
    id: 'alt1',
    label: '✦ Alt A · CDN edge',
    note: 'Moves redirect caching to the CDN edge (cached 301s), pairs Redis with a replica for failover, and hash-partitions Cassandra on the short key — closes both the SPOF and partitioning gaps.',
    flow: [
      [{ name: 'Client', sub: 'web / mobile' }],
      [{ name: 'CDN edge', sub: 'cached 301s' }],
      [{ name: 'Load balancer', sub: 'L7' }],
      [
        { name: 'App servers', sub: 'read + write' },
        { name: 'Key-gen svc', sub: 'base62' },
      ],
      [
        { name: 'Redis + replica', sub: 'auto-failover' },
        { name: 'Cassandra', sub: 'hash-partitioned' },
      ],
    ],
  },
  {
    id: 'alt2',
    label: '✦ Alt B · Postgres replicas',
    note: 'A deliberately simpler stack to contrast: Postgres range-partitioned by key prefix with two read replicas behind the read path. Worth discussing when Cassandra is overkill — interviewers often probe "why not SQL?".',
    flow: [
      [{ name: 'Client', sub: 'web / mobile' }],
      [{ name: 'Load balancer', sub: 'L7' }],
      [
        { name: 'App servers', sub: 'read + write' },
        { name: 'Key-gen svc', sub: 'base62' },
      ],
      [
        { name: 'Postgres primary', sub: 'range-partitioned' },
        { name: 'Read replicas ×2', sub: 'redirect reads' },
      ],
    ],
  },
]

export function HldDiagram(props: {
  submissionId: string
  comments?: ReviewComment[]
  anchorRef?: React.Ref<HTMLDivElement>
}): ReactNode {
  const { submissionId, comments = [], anchorRef } = props
  const { openComposer, attachments, toggleAttachment } =
    useCoachingStore().review
  const sectionComments = comments.filter((c) => c.section === 'hld')
  const [view, setView] = useState<string>('client')

  const curAlt = ALTS.find((a) => a.id === view) ?? null
  const flow = curAlt ? curAlt.flow : CLIENT_FLOW
  const altShown = curAlt != null
  const attachKey = curAlt ? `hld-alt:${submissionId}:${curAlt.id}` : null
  const attached = attachKey ? (attachments[attachKey] ?? false) : false

  function handleBoxClick(name: string) {
    openComposer(
      'hld',
      `${altShown ? 'Alt design' : 'High-level'} · ${name}`,
      'critique',
    )
  }

  return (
    <SectionCard
      title="High-level design"
      commentCount={sectionComments.length}
      onAddComment={() =>
        openComposer('hld', 'High-level · Overall', 'critique')
      }
      anchorRef={anchorRef}
    >
      {sectionComments.map((c) => (
        <CritiqueFlag key={c.id} comment={c} />
      ))}

      <div className="mb-2.5 flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => setView('client')}
          className="rounded-full px-3.5 py-1 text-[11.5px] font-semibold"
          style={
            !curAlt
              ? { background: 'var(--cd-text)', color: 'var(--cd-bg)' }
              : {
                  background: 'transparent',
                  color: 'var(--cd-text-muted)',
                  border: '1px solid var(--cd-border-2)',
                }
          }
        >
          Client's design
        </button>
        {ALTS.map((alt) => {
          const active = curAlt?.id === alt.id
          return (
            <button
              key={alt.id}
              type="button"
              onClick={() => setView(alt.id)}
              className="rounded-full px-3.5 py-1 text-[11.5px] font-semibold"
              style={
                active
                  ? {
                      background:
                        'color-mix(in oklab, var(--cd-purple) 14%, transparent)',
                      color: 'var(--cd-purple)',
                      border: '1px solid var(--cd-purple)',
                    }
                  : {
                      background: 'transparent',
                      color: 'var(--cd-text-muted)',
                      border: '1px solid var(--cd-border-2)',
                    }
              }
            >
              {alt.label}
            </button>
          )
        })}
        <span className="ml-auto text-[11.5px] text-(--cd-text-faint)">
          {altShown
            ? 'AI-drafted alternative — attach it to your review below.'
            : 'Click any component to comment on it.'}
        </span>
      </div>

      <div
        className="flex flex-wrap items-center gap-1.5 rounded-[10px] border bg-(--cd-bg) px-3 py-4"
        style={{
          borderColor: altShown
            ? 'color-mix(in oklab, var(--cd-purple) 30%, transparent)'
            : 'var(--cd-border)',
        }}
      >
        {flow.map((group, i) => (
          <div
            key={group.map((b) => b.name).join('|')}
            className="flex items-center gap-1.5"
          >
            {i > 0 && (
              <span className="text-[15px] text-(--cd-text-ghost)">→</span>
            )}
            <div className="flex flex-col gap-2">
              {group.map((box) => (
                <button
                  key={box.name}
                  type="button"
                  onClick={() => handleBoxClick(box.name)}
                  className="min-w-[72px] rounded-[9px] border border-(--cd-border-3) bg-(--cd-inset) px-2.5 py-2 text-center"
                >
                  <div className="text-[12.5px] font-semibold whitespace-nowrap">
                    {box.name}
                  </div>
                  <div className="text-[10.5px] whitespace-nowrap text-(--cd-text-faint)">
                    {box.sub}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {curAlt && attachKey && (
        <div
          className="mt-2.5 flex flex-wrap items-center gap-2.5 rounded-[10px] border px-3.5 py-2.5"
          style={{
            background: 'color-mix(in oklab, var(--cd-purple) 5%, transparent)',
            borderColor:
              'color-mix(in oklab, var(--cd-purple) 22%, transparent)',
          }}
        >
          <span
            className="shrink-0 rounded-full px-2.5 py-0.5 text-[10.5px] font-bold text-(--cd-purple)"
            style={{
              background:
                'color-mix(in oklab, var(--cd-purple) 14%, transparent)',
            }}
          >
            ✦ Suggestion
          </span>
          <span className="min-w-[240px] flex-1 text-[12.5px] leading-[1.55] text-(--cd-text-2)">
            {curAlt.note}
          </span>
          <button
            type="button"
            onClick={() => toggleAttachment(attachKey)}
            className="shrink-0 rounded-full border px-3.5 py-1 text-xs font-semibold"
            style={
              attached
                ? {
                    background:
                      'color-mix(in oklab, var(--cd-green) 14%, transparent)',
                    color: 'var(--cd-green)',
                    borderColor:
                      'color-mix(in oklab, var(--cd-green) 40%, transparent)',
                  }
                : {
                    background: 'transparent',
                    color: 'var(--cd-text-muted)',
                    borderColor: 'var(--cd-border-2)',
                  }
            }
          >
            {attached ? '✓ Attached to review' : 'Attach to review'}
          </button>
        </div>
      )}
    </SectionCard>
  )
}
